package com.evoapps.evoshift.viewmodels

import com.evoapps.evoshift.domain.usecase.finance.spmh
import com.evoapps.evoshift.domain.util.AppClock
import com.evoapps.evoshift.observability.AnalyticsEvent
import com.evoapps.evoshift.observability.AppTelemetry

import com.evoapps.evoshift.domain.usecase.billing.ObservePlanActiveUseCase
import com.evoapps.evoshift.domain.model.currentOrNearest
import com.evoapps.evoshift.domain.usecase.finance.GetMonthTrendUseCase
import com.evoapps.evoshift.domain.usecase.finance.MonthTrend
import com.evoapps.evoshift.domain.usecase.finance.DayTrend
import com.evoapps.evoshift.domain.usecase.finance.GetStaffingHintsUseCase
import com.evoapps.evoshift.domain.staffing.DayStaffingHint
import com.evoapps.evoshift.domain.staffing.StaffingVerdict
import com.evoapps.evoshift.domain.repository.WorkplaceRepository
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.toUiText
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.error_user_not_found
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.model.Capability
import com.evoapps.evoshift.domain.model.capabilitiesAt
import com.evoapps.evoshift.domain.model.capabilities
import com.evoapps.evoshift.domain.usecase.AtWorkEntry
import com.evoapps.evoshift.domain.usecase.GetManagerDashboardUseCase
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import com.evoapps.evoshift.domain.usecase.roster.GetRosterSummaryUseCase
import com.evoapps.evoshift.session.WorkplaceMonthSelection
import com.evoapps.evoshift.session.WorkplaceSessionManager
import com.evoapps.evoshift.session.of
import com.evoapps.evoshift.domain.usecase.ObserveRosterChangesUseCase
import evoshift.composeapp.generated.resources.daily_result_saved
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.combine
import kotlinx.datetime.YearMonth
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.collectLatest
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.merge
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.async
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.launch
import com.evoapps.evoshift.domain.usecase.staff.GetTeamReadinessUseCase
import com.evoapps.evoshift.domain.usecase.staff.StaffAlerts
import kotlinx.datetime.TimeZone
import kotlinx.datetime.number
import kotlinx.datetime.toLocalDateTime
import com.evoapps.evoshift.domain.util.AppClock.now
import kotlinx.datetime.LocalDate
import kotlinx.datetime.todayIn
import com.evoapps.evoshift.domain.model.DailyRecord
import com.evoapps.evoshift.domain.model.LabourBase
import com.evoapps.evoshift.domain.usecase.daily.FindMissingDailyResultsUseCase
import com.evoapps.evoshift.domain.usecase.daily.SubmitDailyRevenueUseCase

data class OwnerDashboardUiState(
    /** SH-226: "Weather in the coming days" (off: no card). */
    val weather: UpcomingWeatherState = UpcomingWeatherState(),
    val isLoading: Boolean = true,
    val userName: String = "",
    val workplaceName: String = "",
    /** The workplace's currency, for every amount on the dashboard. */
    val currency: String = DEFAULT_CURRENCY,
    /** SH-95: the month's labour % of income (of net sales without a commission). */
    val laborCostPercentage: Double = 0.0,
    /** SH-95: what [laborCostPercentage] and the charts' labour share are measured against. */
    val laborBase: LabourBase = LabourBase.SALES,
    /** SH-95: the workplace's labour target (% of income), the charts' reference line; null when unknown. */
    val labourTargetPercent: Double? = null,
    val laborCostTrend: Int = 0, // -1, 0, 1 (0 until there is history to compare with)
    /** This month, plan and actual combined; null when nothing is calculated yet. */
    val statsMonth: Int? = null,
    /** SH-147: the shown month's roster (its missing-pay warning); null without one. */
    val statsRosterId: String? = null,
    val revenue: Double? = null,
    val laborCost: Double? = null,
    val activeEmployees: List<String> = emptyList(),
    val upcomingEmployees: List<String> = emptyList(),
    val completedEmployees: List<String> = emptyList(),
    val upcomingDate: String? = null,
    /** The month's days for the revenue / labour chart. */
    val dailyTrend: List<DayTrend> = emptyList(),
    /** SH-152: the labour % was added up from [dailyTrend] (so its explanation lists the days' people). */
    val labourFromDays: Boolean = false,
    /**
     * SH-191: the month's SPMH (sales per man-hour) beside its labour %: the takings and targets of
     * [dailyTrend] ÷ their hours (worked where the takings are in, else planned); null without figures.
     */
    val spmh: Double? = null,
    /** SH-152: the team's names by user id, for the explanation. */
    val names: Map<String, String> = emptyMap(),
    /** Up to six months ending with this one, for the labour-share trend. */
    val monthTrend: List<MonthTrend> = emptyList(),
    /** Today's assigned shifts with clock-in/out and status, by planned start (SH-72, managers only). */
    val todayAtWork: List<AtWorkEntry> = emptyList(),
    /** What the person may do at this workplace: the money shows with VIEW_FINANCES only. */
    val capabilities: Set<Capability> = emptySet(),
    /**
     * SH-68: yesterday's and today's days still without takings, yesterday first: the dashboard
     * asks "Fill in the day's result?" for the first one, with the amount right in the card.
     */
    val missingResults: List<DailyRecord> = emptyList(),
    /** Today (ISO), to call the asked day "today" or "yesterday". */
    val today: String? = null,
    /** Why the last takings typed in that card were not saved (the card is back). */
    val dailyResultError: UiText? = null,
    /** SH-47: people whose medical exam or OHS training expired or ends within 30 days. */
    val staffAlerts: StaffAlerts = StaffAlerts(),
    /**
     * SH-54: the shown month's days from today on that are outside the labour target (the action
     * items), with the roster they belong to; null when there is nothing to show (no money, a past
     * month, no roster).
     */
    val staffing: StaffingActions? = null,
    /**
     * SH-131: the shown month across the workplaces the person owns (two or more), shared costs
     * counted once (inside the workplaces' results); null otherwise.
     */
    val business: com.evoapps.evoshift.domain.usecase.owner.BusinessMonth? = null,
    val error: UiText? = null,
    /** SH-188: the first load failed and nothing was shown yet: Try again instead of an empty dashboard. */
    val loadError: UiText? = null,
    /** Something has been shown; a later failure is a message over it, not Try again. */
    val loaded: Boolean = false,
    /** SH-188: a one-off confirmation for the snackbar (the takings were saved). */
    val notice: UiText? = null,
    /**
     * SH-225: the shown month's sales against last week (week to date) and last year by weekday
     * (month to date), like for like: the chips on the revenue card; null without money or data.
     */
    val salesComparison: com.evoapps.evoshift.domain.usecase.finance.DashboardSalesComparison? = null
)

/** SH-54: the days of [rosterId] still to come whose staffing is outside the target band. */
data class StaffingActions(
    val rosterId: String,
    val days: List<DayStaffingHint>
) {
    val tooFew: Int get() = days.count { it.verdict == StaffingVerdict.TOO_FEW }
    val tooMany: Int get() = days.count { it.verdict == StaffingVerdict.TOO_MANY }
}

class OwnerDashboardViewModel(
    private val getCurrentUserUseCase: GetCurrentUserUseCase,
    private val getRosterSummaryUseCase: GetRosterSummaryUseCase,
    private val getManagerDashboardUseCase: GetManagerDashboardUseCase, // Reusing Manager UC for now as it has financials
    private val sessionManager: WorkplaceSessionManager,
    /** For the charts; without it the dashboard shows the figures only. */
    private val getMonthTrendUseCase: GetMonthTrendUseCase? = null,
    /** For the workplace's currency; without it amounts show in PLN. */
    private val workplaceRepository: WorkplaceRepository? = null,
    /** SH-68: the days whose takings are missing; without it the dashboard does not ask. */
    private val findMissingDailyResultsUseCase: FindMissingDailyResultsUseCase? = null,
    private val submitDailyRevenueUseCase: SubmitDailyRevenueUseCase? = null,
    private val today: () -> LocalDate = { AppClock.todayIn(TimeZone.currentSystemDefault()) },
    private val observePlanActive: ObservePlanActiveUseCase? = null,
    private val getTeamReadiness: GetTeamReadinessUseCase? = null,
    /**
     * SH-89: the app-wide month of the top bar; the figures and charts are that month's (the team's
     * day stays today's). Without it, the current month or the nearest one with a roster.
     */
    private val monthSelection: WorkplaceMonthSelection? = null,
    /** SH-54: the month's staffing hints (counted as action items); without it there are none. */
    private val getStaffingHints: GetStaffingHintsUseCase? = null,
    /** SH-131: the month across the owner's workplaces; without it the dashboard shows one workplace. */
    private val getBusinessMonth: com.evoapps.evoshift.domain.usecase.owner.GetBusinessMonthUseCase? = null,
    /** SH-152: the team's names for the labour explanation. */
    private val getAssignableEmployees: com.evoapps.evoshift.domain.usecase.user.GetAssignableEmployeesUseCase? = null,
    /** SH-188: the shown months' changes (shifts, recomputed totals); without it the dashboard reads once. */
    private val observeRosterChanges: ObserveRosterChangesUseCase? = null,
    /** SH-226: the forecast card; without it (or with weather off) there is none. */
    observeWeather: com.evoapps.evoshift.domain.usecase.weather.ObserveWorkplaceWeatherUseCase? = null,
    /** SH-225: the comparison chips' reads; without it the revenue card has none. */
    private val getSalesComparison: com.evoapps.evoshift.domain.usecase.finance.GetSalesComparisonUseCase? = null
) : ViewModel() {

    private val upcomingWeather = UpcomingWeather(observeWeather, workplaceRepository, today)

    /** SH-225: the revenue card's comparison chips (last year's reads kept between loads). */
    private val comparisonChips = SalesComparisonChips(getSalesComparison, workplaceRepository)

    private val _uiState = MutableStateFlow(OwnerDashboardUiState())
    val uiState = _uiState.asStateFlow()

    private val watching = JobSlot()
    private var watched: Triple<String, Set<String>, Boolean>? = null

    init {
        observeSession()
    }

    /**
     * SH-188: the months on screen are followed: a change to their shifts or totals (takings or
     * costs written here or elsewhere, a swap, the server's recomputed figures) reads the
     * dashboard again, quietly, behind the figures shown. So the KPIs are current when the person
     * comes back from the daily results, the roster or the labour cost.
     */
    private fun watch(workplaceId: String, rosterIds: Set<String>, money: Boolean) {
        val observe = observeRosterChanges ?: return
        val key = Triple(workplaceId, rosterIds, money)
        if (key == watched) return
        watched = key
        if (rosterIds.isEmpty()) {
            watching.cancel()
            return
        }
        watching.launch(viewModelScope) {
            rosterIds.map { observe(it, money) }.merge()
                .catch { e -> _uiState.update { it.copy(error = e.toUiText()) } }
                .collect { viewModelScope.launch { loadData(workplaceId, shownMonth) } }
        }
    }

    fun clearError() = _uiState.update { it.copy(error = null) }

    /** The confirmation was shown (SH-188). */
    fun noticeShown() = _uiState.update { it.copy(notice = null) }

    private fun observeSession() {
        viewModelScope.launch {
            val selected = monthSelection?.month
            if (selected == null) {
                sessionManager.currentWorkplaceId.collectLatest { workplaceId ->
                    if (workplaceId != null) loadData(workplaceId, null)
                }
            } else {
                sessionManager.currentWorkplaceId.combine(selected) { workplaceId, month -> workplaceId to month }
                    .collectLatest { (workplaceId, month) -> if (workplaceId != null) loadData(workplaceId, month) }
            }
        }
    }

    /** The month shown, for Try again. */
    private var shownMonth: YearMonth? = null

    /**
     * SH-188: a read that throws (a refused listener, offline without a cache) is the screen's
     * error, never an ended coroutine: the session is still followed afterwards.
     */
    private suspend fun loadData(workplaceId: String, month: YearMonth?) {
        shownMonth = month
        try {
            load(workplaceId, month)
        } catch (e: kotlinx.coroutines.CancellationException) {
            throw e
        } catch (e: Exception) {
            failedToLoad(e)
        }
    }

    /** Nothing shown yet: Try again; over the figures shown: a message. */
    private fun failedToLoad(e: Throwable) {
        _uiState.update {
            if (it.loaded) it.copy(isLoading = false, error = e.toUiText())
            else it.copy(isLoading = false, loadError = e.toUiText())
        }
    }

    /** SH-188: Try again after the first load failed. */
    fun retry() {
        val workplaceId = sessionManager.currentWorkplaceId.value ?: return
        _uiState.update { it.copy(loadError = null, isLoading = !it.loaded) }
        viewModelScope.launch { loadData(workplaceId, shownMonth) }
    }

    /** Only the first load shows the spinner; a workplace switch keeps the old figures until the new arrive. */
    private suspend fun load(workplaceId: String, month: YearMonth?) {
        val user = getCurrentUserUseCase().first()
        if (user == null) {
            _uiState.update { it.copy(isLoading = false, error = UiText.Resource(Res.string.error_user_not_found)) }
            return
        }

        val engagement = user.engagements[workplaceId]
        val workplaceName = engagement?.cachedWorkplaceName.orEmpty()
        val capabilities = user.capabilitiesAt(workplaceId, observePlanActive.activeNow(workplaceId))
        // SH-226: the forecast for the coming days (nothing is read with weather off).
        upcomingWeather.follow(viewModelScope, workplaceId) { weather -> _uiState.update { it.copy(weather = weather) } }
        val money = Capability.VIEW_FINANCES in capabilities
        val currency = if (money) workplaceRepository.currencyOf(workplaceId) else DEFAULT_CURRENCY

        _uiState.update {
            it.copy(
                userName = user.displayName,
                workplaceName = workplaceName,
                currency = currency,
                capabilities = capabilities
            )
        }
        // Without access to the workplace's figures (e.g. the engagement ended) nothing is read.
        if (Capability.EDIT_ROSTER !in capabilities) {
            _uiState.update { it.copy(isLoading = false, missingResults = emptyList()) }
            return
        }

        // SH-47: medical exams and OHS training expired or ending within 30 days (statuses only).
        val alerts = if (capabilities.shows(Gate.STAFF_STATUS)) getTeamReadiness?.alerts(workplaceId)?.getOrNull() else null
        _uiState.update { it.copy(staffAlerts = alerts ?: StaffAlerts()) }

        val now = now().toLocalDateTime(TimeZone.currentSystemDefault())
        val currentMonth = now.month.number
        val currentYear = now.year

        val rosters = getRosterSummaryUseCase(workplaceId).first()
        val todayRoster = rosters.currentOrNearest(currentYear, currentMonth)
        // The chosen month exactly (an empty month shows no figures), else the current one.
        val targetRoster = if (month != null) rosters.of(month) else todayRoster
        watch(workplaceId, setOfNotNull(targetRoster?.id, todayRoster?.id), money)

        // SH-68: takings are money; the prompt is for those who may see it, and only for months
        // that exist. A failed lookup just does not ask.
        val day = today()
        val missing = findMissingDailyResultsUseCase?.takeIf { money }
            ?.invoke(workplaceId, rosters.map { it.id }.toSet(), day)?.getOrNull().orEmpty()
        _uiState.update { it.copy(missingResults = missing, today = day.toString(), dailyResultError = null) }

        // SH-131: the whole business in the shown month (owners of two or more workplaces).
        val businessMonth = month ?: today().let { YearMonth(it.year, it.month) }
        _uiState.update { it.copy(business = if (money) businessOf(user, businessMonth) else null) }

        if (targetRoster != null) {
            // Last month's figures, for the trend arrow (lower labour share is better); both
            // months load at the same time.
            val previousRoster = rosters
                .filter { it.year * 100 + it.month < targetRoster.year * 100 + targetRoster.month }
                .maxByOrNull { it.year * 100 + it.month }
            val (result, previousPercentage, todayData) = coroutineScope {
                val current = async { getManagerDashboardUseCase(targetRoster.id, includeFinances = money) }
                // Another month chosen: who works today still comes from today's month.
                val today = async {
                    todayRoster?.takeIf { it.id != targetRoster.id }
                        ?.let { getManagerDashboardUseCase(it.id, includeFinances = false).getOrNull() }
                }
                val previous = async {
                    previousRoster?.takeIf { money }
                        ?.let { getManagerDashboardUseCase(it.id).getOrNull()?.monthlyStats?.combined }
                        ?.labourPercent
                }
                Triple(current.await(), previous.await(), today.await())
            }
            result.onSuccess { monthData ->
                val data = todayData ?: monthData
                val summary = monthData.monthlyStats?.combined
                val laborPercentage = summary?.labourPercent ?: 0.0

                _uiState.update {
                    it.copy(
                        isLoading = false,
                        loaded = true,
                        loadError = null,
                        laborCostPercentage = laborPercentage,
                        laborBase = summary?.labourBase ?: LabourBase.SALES,
                        laborCostTrend = laborTrend(laborPercentage, previousPercentage),
                        statsMonth = targetRoster.month,
                        statsRosterId = targetRoster.id,
                        revenue = summary?.revenue,
                        laborCost = summary?.laborCost,
                        activeEmployees = data.activeEmployees,
                        upcomingEmployees = data.upcomingEmployees,
                        completedEmployees = data.completedEmployees,
                        upcomingDate = data.upcomingDate,
                        todayAtWork = data.todayAtWork
                    )
                }
            }.onFailure { e -> failedToLoad(e) }
            // Charts follow the figures: this month's days and the last six months side by side.
            getMonthTrendUseCase?.takeIf { money }?.let { trend ->
                val targetKey = targetRoster.year * 100 + targetRoster.month
                val recent = rosters.filter { it.year * 100 + it.month <= targetKey }
                    .sortedByDescending { it.year * 100 + it.month }
                    .take(6)
                    .map { it.id }
                val (days, months) = coroutineScope {
                    val days = async { trend(targetRoster.id).getOrNull().orEmpty() }
                    val months = async { trend.months(recent) }
                    days.await() to months.await()
                }
                val target = workplaceRepository.labourTargetOf(workplaceId)
                val names = getAssignableEmployees?.invoke(workplaceId)?.getOrNull().orEmpty().associate { it.uid to it.displayName }
                _uiState.update { state ->
                    // SH-147: the labour cost and % as the days work it out now (the stored totals lag
                    // edits until the month is published again), as on the Money screen.
                    // Only over stored totals: a month without any keeps "no data yet".
                    val live = days.takeIf { it.isNotEmpty() && state.laborCost != null }?.let { d -> d.sumOf { it.labor } to d.sumOf { it.income } }
                    val livePercent = live?.let { (labour, income) -> com.evoapps.evoshift.domain.model.LabourShare.percent(labour, income) }
                    state.copy(
                        dailyTrend = days,
                        labourFromDays = livePercent != null,
                        spmh = days.takeIf { it.isNotEmpty() && state.laborCost != null }?.spmh(com.evoapps.evoshift.domain.explain.Side.FORECAST),
                        names = names,
                        monthTrend = months,
                        labourTargetPercent = target?.percent,
                        laborCost = live?.first ?: state.laborCost,
                        laborCostPercentage = live?.let { (labour, income) -> com.evoapps.evoshift.domain.model.LabourShare.percent(labour, income) } ?: state.laborCostPercentage
                    )
                }
                // SH-225: the revenue card's chips: this week and this month against last week and last year.
                _uiState.update { it.copy(salesComparison = comparisonChips.load(workplaceId, LocalDate(targetRoster.year, targetRoster.month, 1), day)) }
                // SH-54: the days still to come outside the labour target, from the same days.
                val staffing = getStaffingHints?.of(targetRoster.id, days)?.getOrNull()
                    ?.takeIf { days.isNotEmpty() && days.last().date >= day }
                _uiState.update { it.copy(staffing = staffing?.let { s -> StaffingActions(targetRoster.id, s.actionable(day)) }) }
            }
        } else {
            // Nothing to show for this month: drop the previous figures; who works today stays.
            val todayData = todayRoster?.let { getManagerDashboardUseCase(it.id, includeFinances = false).getOrNull() }
            _uiState.update {
                it.copy(
                    isLoading = false,
                    loaded = true,
                    loadError = null,
                    laborCostPercentage = 0.0,
                    laborCostTrend = 0,
                    statsMonth = null,
                    statsRosterId = null,
                    revenue = null,
                    laborCost = null,
                    activeEmployees = todayData?.activeEmployees.orEmpty(),
                    upcomingEmployees = todayData?.upcomingEmployees.orEmpty(),
                    completedEmployees = todayData?.completedEmployees.orEmpty(),
                    upcomingDate = todayData?.upcomingDate,
                    todayAtWork = todayData?.todayAtWork.orEmpty(),
                    dailyTrend = emptyList(),
                    labourFromDays = false,
                    spmh = null,
                    monthTrend = emptyList(),
                    staffing = null,
                    salesComparison = null
                )
            }
        }
    }

    /**
     * SH-68: the takings (net) of [date] typed in the dashboard's card. The card goes at once (the
     * next missing day, if any, takes its place); if the save fails it comes back with the reason.
     */
    fun onSubmitDailyResult(date: String, amount: Double) {
        if (!_uiState.value.capabilities.shows(Gate.MONEY)) return
        val submit = submitDailyRevenueUseCase ?: return
        val record = _uiState.value.missingResults.firstOrNull { it.date == date } ?: return
        if (amount < 0) return
        _uiState.update { state -> state.copy(missingResults = state.missingResults.filter { it.date != date }, dailyResultError = null) }
        viewModelScope.launch {
            submit(record.workplaceId, record.rosterId, date, amount).onSuccess {
                AppTelemetry.log(AnalyticsEvent.DailyResultEntered)
                // SH-240: a success moment for the store rating prompt.
                com.evoapps.evoshift.review.AppReviewPrompt.onSuccess(com.evoapps.evoshift.domain.review.SuccessMoment.TAKINGS_ENTERED, record.workplaceId)
                // SH-188: said, and the figures next to the row read again (the server's recomputed
                // totals follow through the month's listener).
                _uiState.update { it.copy(notice = UiText.Resource(Res.string.daily_result_saved)) }
                loadData(record.workplaceId, shownMonth)
            }.onFailure { e ->
                _uiState.update { state ->
                    state.copy(
                        missingResults = (state.missingResults + record).distinctBy { it.date }.sortedBy { it.date },
                        dailyResultError = e.toUiText()
                    )
                }
            }
        }
    }

    /** The workplaces [user] owns (their own, not a co-owner's), when there are two or more, in [month]. */
    private suspend fun businessOf(user: com.evoapps.evoshift.domain.model.User, month: YearMonth): com.evoapps.evoshift.domain.usecase.owner.BusinessMonth? {
        val business = getBusinessMonth ?: return null
        val repository = workplaceRepository ?: return null
        val owned = user.engagements.values
            .filter { it.capabilities.shows(Gate.RECURRING_COSTS) }
            .map { it.workplaceId }
            .filter { id -> repository.getWorkplace(id).getOrNull()?.ownerId == user.uid }
        if (owned.size < 2) return null
        return business(owned, month.year, month.month.number).getOrNull()
    }

    companion object {
        /** Labour cost as a share of [revenue] (whatever it is measured against), in percent; 0 without it. */
        fun laborShare(laborCost: Double, revenue: Double): Double = if (revenue > 0) laborCost / revenue * 100 else 0.0

        /**
         * -1 when the labour share fell by at least half a point since last month (good), 1 when it
         * rose, 0 when it barely moved or a month has no figures.
         */
        fun laborTrend(current: Double, previous: Double?): Int = when {
            previous == null || previous <= 0.0 || current <= 0.0 -> 0
            current <= previous - 0.5 -> -1
            current >= previous + 0.5 -> 1
            else -> 0
        }
    }
}
